'use client';
/**
 * Les éditeurs AVANCÉS du paramétrage du manuel — champs, affichages, statuts,
 * livret, forfait — rapatriés de l'atelier le 18/08/2026. C'est la fin du
 * rapatriement : les boutons « Ouvrir dans l'atelier » disparaissent avec lui.
 *
 * ── La règle d'écriture, commune aux cinq ───────────────────────────────────
 *
 * Chaque éditeur travaille sur un BROUILLON local et n'écrit qu'au bouton
 * « Enregistrer » — jamais au clic. Deux raisons, toutes deux vécues :
 *
 *  · le moteur écrit les clés reçues mais remplace chaque clé ENTIÈRE ; écrire
 *    à chaque coche multiplierait les fenêtres où deux écrans ouverts se
 *    marchent dessus, la dernière écriture emportant tout ;
 *  · réordonner des champs, c'est dix clics — dix écritures et dix
 *    rechargements rendraient l'écran inutilisable.
 *
 * À l'enregistrement, l'éditeur envoie SES clés, complètes, et rien d'autre.
 *
 * ── D'où viennent les listes ────────────────────────────────────────────────
 *
 * Le catalogue des champs, ses sections et ses types viennent de
 * `@/shared/catalogueChamps` — la même source que l'atelier, vérifiée contre le
 * seed du backend par `audit-champs-fiche`. Les listes recopiées ici seraient
 * reparties en dérive au premier champ ajouté de l'autre côté.
 */
import React, { useState } from 'react';
import { card, EmptyState, useConfirmation } from '@/components/ui';

/**
 * Les défauts de visibilité, RECOPIÉS du moteur (`src/visibilite-champs.ts`).
 *
 * ⚠️ Recopiés à dessein, et c'est le seul endroit où ils le sont : l'écran doit
 * afficher la valeur qui S'APPLIQUE quand rien n'est posé, sinon il montrerait
 * « Public » sur un champ que le serveur ferme — et l'on croirait le prix de
 * sous-traitance exposé alors qu'il ne l'est pas. Le serveur reste seul juge :
 * ces valeurs ne sont jamais envoyées, elles ne servent qu'à l'affichage.
 */
/* Copie de `backend/src/visibilite-champs.ts` (DEFAUTS). Depuis le 16/10/2026
   le défaut GÉNÉRAL est « correspondant » : tout champ non réglé est réservé
   aux comptes Correspondant connectés ; seuls les champs ci-dessous sont
   publics (liste courte patients), et prix/sous-traitance exigent un compte
   relié à un LABORATOIRE du carnet. */
const PUBLICS = [
  'libelle', 'famille', 'sousFamille', 'synonymes',
  'visiblePatients', 'patientUtilite', 'patientPreparation',
  'milieux', 'preanalytique', 'delaiRendu', 'delaiRenduHeures', 'joursRealisation',
  'cotation', 'cnam', 'archivee',
];
const VISIBILITE_DEFAUT: Record<string, string> = {
  ...Object.fromEntries(PUBLICS.map((c) => [c, 'public'])),
  prixST: 'laboratoire',
  sousTraitance: 'laboratoire',
  remarqueImportante: 'interne',
};
const VISIBILITE_GENERALE = 'correspondant';
/** L'ancienne graphie, encore en base sur des réglages d'avant le 16/10 : elle
 *  valait « tout compte partenaire », et ces comptes étaient des laboratoires. */
const niveauAffiche = (v: string | undefined, cle: string) =>
  (v === 'confrere' ? 'laboratoire' : v) || VISIBILITE_DEFAUT[cle] || VISIBILITE_GENERALE;
import {
  FIELD_CATALOG, FIELD_SECTION_ORDER, CONFIGURABLE_TYPE_OPTIONS, SPECIAL_TYPES,
  type FieldDef, type FieldType,
} from '@/shared/catalogueChamps';

import { Bouton } from '@/shared/Bouton';
import { Segment } from '@/shared/Segment';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Cfg = any;
type Ecrire = (corps: Record<string, unknown>, quoi: string) => void;
type Props = { p: Cfg; estRq: boolean; envoi: boolean; ecrire: Ecrire };

const etiquette: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 6,
};
const indice: React.CSSProperties = { fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 };

/**
 * ⚠️ DÉFINIE AU NIVEAU DU MODULE, jamais dans le rendu (02/09/2026).
 *
 * `Zone` était déclarée à l'intérieur de son parent : React voyait donc un
 * TYPE DE COMPOSANT DIFFÉRENT à chaque rendu, et démontait puis remontait
 * l'arbre entier au lieu de le mettre à jour. Sur un formulaire de cases à
 * cocher, cela se paie en focus perdu et en état réinitialisé — et le défaut
 * ne se voit qu'à l'usage, jamais à la relecture.
 *
 * Ce que le parent lui donnait par fermeture (`estRq`, `cur`, `poserZone`)
 * passe désormais en propriétés : c'est plus long à écrire, et c'est la seule
 * façon d'avoir un type stable.
 */
function Zone({ zone, titre, items, estRq, coche, onCocher }: {
  zone: 'summary' | 'right' | 'fiche'; titre: string;
  items: { key: string; label: string }[];
  estRq: boolean;
  coche: (zone: 'summary' | 'right' | 'fiche', key: string) => boolean;
  onCocher: (zone: 'summary' | 'right' | 'fiche', key: string, valeur: boolean) => void;
}) {
  return (
    <div style={{ flex: '1 1 220px', minWidth: 0 }}>
      <div style={etiquette}>{titre}</div>
      {items.map((it) => (
        <label key={it.key} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '2px 0', fontSize: 12.5, cursor: 'pointer' }}>
          <input type="checkbox" disabled={!estRq} checked={coche(zone, it.key)}
            onChange={(e) => onCocher(zone, it.key, e.currentTarget.checked)} />
          {it.label}
        </label>
      ))}
    </div>
  );
}

/** Même raison que `Zone` : un composant déclaré dans un rendu est un type neuf
 *  à chaque passe, donc un remontage. */
function Bascule({ cle, label, estRq, actif, onBasculer }: {
  cle: string; label: string; estRq: boolean;
  actif: boolean; onBasculer: (cle: string, valeur: boolean) => void;
}) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '2px 0', fontSize: 12.5, cursor: 'pointer' }}>
      <input type="checkbox" disabled={!estRq} checked={actif}
        onChange={(e) => onBasculer(cle, e.currentTarget.checked)} />
      {label}
    </label>
  );
}

function BarreEnregistrer({ envoi, disabled, onClick, quoi }: {
  envoi: boolean; disabled?: boolean; onClick: () => void; quoi: string;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
      <Bouton variante="primaire" disabled={envoi || disabled} onClick={onClick}>
        Enregistrer {quoi}
      </Bouton>
      <span style={indice}>Rien n’est écrit avant ce bouton — les réglages ci-dessus sont un brouillon.</span>
    </div>
  );
}

/* ═════════ 1. CHAMPS — activation, obligation, type, ordre, sections ═════ */

const SPECIAL_LABELS: Record<string, string> = {
  recipient: 'Récipient (normalisé)', famille: 'Famille (liste labo)',
  lieu: 'Lieu (sur place / sous-traité)', syn: 'Multi-valeurs',
};
const COLONNES_SECTION = [
  { value: 'full', label: 'Pleine largeur' }, { value: '1', label: 'Colonne 1' }, { value: '2', label: 'Colonne 2' },
];

/** La clé d'un champ ajouté, dérivée du libellé UNE fois à la création — elle
 *  ne suit pas les renommages : la clé voyage dans les données des fiches. */
function cleDepuisLibelle(libelle: string, prises: Set<string>): string {
  const base = libelle.normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 24) || 'champ';
  let cle = base; let n = 2;
  while (prises.has(cle)) cle = `${base}_${n++}`;
  return cle;
}

export function OngletChamps({ p, estRq, envoi, ecrire }: Props) {
  const { demander, confirmation } = useConfirmation();
  // Le brouillon : chaque clé démarre sur l'état en base et s'écrit COMPLÈTE.
  const [actifs, setActifs] = useState<Record<string, boolean>>({ ...(p.activeFields || {}) });
  const [obligatoires, setObligatoires] = useState<Record<string, boolean>>({ ...(p.requiredFields || {}) });
  const [types, setTypes] = useState<Record<string, string>>({ ...(p.fieldTypes || {}) });
  /* `fieldOrder` est un objet PAR SECTION (`{ 'Identité': ['libelle', …] }`),
     pas une liste plate — c'est la forme que l'atelier écrit depuis toujours,
     et la première version de cet éditeur, qui supposait une liste, plantait
     l'écran entier au premier rendu. Une section absente = l'ordre du
     catalogue. */
  const [ordre, setOrdre] = useState<Record<string, string[]>>(
    p.fieldOrder && !Array.isArray(p.fieldOrder) ? { ...p.fieldOrder } : {},
  );
  const [ordreSections, setOrdreSections] = useState<string[]>(
    Array.isArray(p.sectionOrder) && p.sectionOrder.length ? [...p.sectionOrder] : [...FIELD_SECTION_ORDER],
  );
  const [surcharges, setSurcharges] = useState<Record<string, string>>({ ...(p.fieldSectionOverride || {}) });
  const [colonnes, setColonnes] = useState<Record<string, string | number>>({ ...(p.sectionCol || {}) });
  const [persos, setPersos] = useState<FieldDef[]>(Array.isArray(p.customFields) ? [...p.customFields] : []);
  const [sectionsPersos, setSectionsPersos] = useState<string[]>(Array.isArray(p.customSections) ? [...p.customSections] : []);
  const [ficheCols, setFicheCols] = useState<number>(p.ficheCols || 2);
  /* ── QUI VOIT CE CHAMP (RQ, 26/08/2026) ───────────────────────────────────
   * « Dans le paramétrage des champs, je voudrais pouvoir définir la visibilité
   * du champ. »
   *
   * ⚠️ Ce réglage n'est PAS un affichage : il est appliqué CÔTÉ SERVEUR, dans
   * le contrôleur de `fiche`. Un champ fermé ne voyage plus dans la réponse
   * JSON — masquer une colonne à l'écran ne l'aurait pas empêchée d'être lue
   * dans l'onglet réseau. C'est ce qui laissait sortir le prix de
   * sous-traitance sur une page publique jusqu'à ce matin. */
  const [visibilite, setVisibilite] = useState<Record<string, string>>({ ...(p.fieldVisibility || {}) });
  const [ajout, setAjout] = useState<Record<string, { libelle: string; type: string }>>({});
  const [nouvelleSection, setNouvelleSection] = useState('');

  const catalogue: FieldDef[] = [...FIELD_CATALOG, ...persos];
  const sections = [...ordreSections, ...sectionsPersos.filter((s) => !ordreSections.includes(s))];
  const sectionDe = (f: FieldDef) => surcharges[f.key] || f.section;
  const champsDe = (sec: string) => {
    const dans = catalogue.filter((f) => sectionDe(f) === sec);
    const voulu = ordre[sec];
    if (!Array.isArray(voulu) || !voulu.length) return dans;
    const rang = (k: string) => { const i = voulu.indexOf(k); return i === -1 ? 10000 : i; };
    return [...dans].sort((a, b) => rang(a.key) - rang(b.key));
  };

  const bouger = (sec: string, key: string, sens: -1 | 1) => {
    /* L'ordre de la section est reconstruit depuis l'ordre AFFICHÉ : c'est la
       seule façon que « monter » fasse ce que l'œil attend, même quand l'ordre
       en base est partiel ou périmé. */
    const affiches = champsDe(sec).map((f) => f.key);
    const i = affiches.indexOf(key); const j = i + sens;
    if (j < 0 || j >= affiches.length) return;
    [affiches[i], affiches[j]] = [affiches[j], affiches[i]];
    setOrdre({ ...ordre, [sec]: affiches });
  };
  const bougerSection = (sec: string, sens: -1 | 1) => {
    const s = [...sections]; const i = s.indexOf(sec); const j = i + sens;
    if (j < 0 || j >= s.length) return;
    [s[i], s[j]] = [s[j], s[i]];
    setOrdreSections(s);
  };
  const ajouterChamp = (sec: string) => {
    const a = ajout[sec]; if (!a?.libelle?.trim()) return;
    const cle = cleDepuisLibelle(a.libelle, new Set(catalogue.map((f) => f.key)));
    setPersos([...persos, { key: cle, label: a.libelle.trim(), section: sec, type: (a.type || 'text') as FieldType, custom: true }]);
    setAjout({ ...ajout, [sec]: { libelle: '', type: 'text' } });
  };
  const retirerChamp = async (key: string) => {
    /* `useConfirmation` et non `window.confirm` (24/08/2026) : la boîte de la
       maison, Échap et le fond ferment. */
    if (!(await demander('Retirer ce champ ajouté ?\nLes valeurs déjà saisies sur les fiches ne sont pas effacées — elles ne seront simplement plus affichées ni saisissables.',
      { confirmer: 'Retirer' }))) return;
    setPersos(persos.filter((f) => f.key !== key));
  };

  const enregistrer = () => {
    /* ⚠️ LES CHAMPS EN COURS DE SAISIE PARTENT AVEC LES AUTRES (09/10/2026). On
       tape « Nouveau champ… » dans une section, on clique « Enregistrer » sans
       passer par « Ajouter le champ » : il n'était dans aucun état que
       l'enregistrement lit, et disparaissait sans un mot. */
    const prises = new Set([...catalogue.map((f) => f.key), ...persos.map((f) => f.key)]);
    const pendants: FieldDef[] = [];
    for (const [sec, a] of Object.entries(ajout)) {
      if (!a?.libelle?.trim()) continue;
      const cle = cleDepuisLibelle(a.libelle, prises);
      prises.add(cle);
      pendants.push({ key: cle, label: a.libelle.trim(), section: sec, type: (a.type || 'text') as FieldType, custom: true });
    }
    const persosFinal = [...persos, ...pendants];
    if (pendants.length) { setPersos(persosFinal); setAjout({}); }
    return ecrire({
      activeFields: actifs, requiredFields: obligatoires, fieldTypes: types,
      fieldOrder: ordre, sectionOrder: sections, fieldSectionOverride: surcharges,
      sectionCol: colonnes, customFields: persosFinal, customSections: sectionsPersos, ficheCols,
      fieldVisibility: visibilite,
    }, 'Champs enregistrés');
  };

  return (
    <div style={{ ...card, padding: 12 }}>
      {confirmation}
      <div style={etiquette}>Champs du catalogue</div>
      <div style={{ ...indice, marginBottom: 10 }}>
        Activez les champs, choisissez leur type, rendez-les obligatoires (cadenas), réglez <strong>qui les
        reçoit</strong>, réordonnez champs et sections. Un champ du <strong>socle</strong> ne se désactive pas —
        le catalogue ne peut pas exister sans lui.
        <br />
        La visibilité est appliquée <strong>côté serveur</strong> : un champ « interne » ne sort pas de
        l’application, même pour qui regarderait la réponse réseau. Le catalogue public reste indexable ;
        ce sont les champs, un par un, qui se referment.
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 12.5 }}>Fiche sur</span>
        <Segment ariaLabel="Colonnes" valeur={ficheCols} onChange={setFicheCols} options={[1, 2, 3].map((n) => ({ valeur: n, libelle: <>{n}colonne{n > 1 ? 's' : ''}</> }))} />
      </div>

      {sections.map((sec) => {
        const champs = champsDe(sec);
        const a = ajout[sec] || { libelle: '', type: 'text' };
        return (
          <div key={sec} style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
              <strong style={{ fontSize: 12.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{sec}</strong>
              <select value={String(colonnes[sec] ?? 'full')} disabled={!estRq}
                onChange={(e) => setColonnes({ ...colonnes, [sec]: e.currentTarget.value === 'full' ? 'full' : Number(e.currentTarget.value) })}
                title="Colonne de cette section dans la fiche" style={{ fontSize: 12.5 }}>
                {COLONNES_SECTION.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 4 }}>
                <Bouton taille="sm" title="Monter la section" onClick={() => bougerSection(sec, -1)}>↑</Bouton>
                <Bouton taille="sm" title="Descendre la section" onClick={() => bougerSection(sec, 1)}>↓</Bouton>
              </span>
            </div>

            {champs.map((f) => {
              const actif = f.core ? true : actifs[f.key] !== false;
              const oblig = f.key === 'libelle' ? true : !!obligatoires[f.key];
              const type = types[f.key] || f.type;
              const options = SPECIAL_TYPES.includes(f.type)
                ? [{ value: f.type, label: SPECIAL_LABELS[f.type] || f.type }, ...CONFIGURABLE_TYPE_OPTIONS]
                : CONFIGURABLE_TYPE_OPTIONS;
              return (
                <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', flexWrap: 'wrap' }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, flex: 1, minWidth: 190, cursor: f.core ? 'default' : 'pointer', opacity: actif ? 1 : 0.55 }}>
                    <input type="checkbox" checked={actif} disabled={!estRq || !!f.core}
                      onChange={(e) => setActifs({ ...actifs, [f.key]: e.currentTarget.checked })} />
                    <span style={{ fontSize: 13, fontWeight: actif ? 600 : 500 }}>{f.label}</span>
                    {f.core && <span style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-faint)' }}>Socle</span>}
                    {f.custom && <span style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', color: 'var(--cyan-700)' }}>Ajouté</span>}
                  </label>
                  <select value={type} disabled={!estRq} style={{ fontSize: 12.5 }}
                    onChange={(e) => setTypes({ ...types, [f.key]: e.currentTarget.value })}>
                    {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <Bouton variante={oblig ? 'primaire' : 'contour'} taille="sm" disabled={!estRq || f.key === 'libelle'} title={f.key === 'libelle' ? 'Toujours obligatoire' : (oblig ? 'Obligatoire — cliquer pour rendre optionnel' : 'Optionnel — cliquer pour rendre obligatoire')} onClick={() => setObligatoires({ ...obligatoires, [f.key]: !oblig })}>
                    {oblig ? '🔒' : '🔓'}
                  </Bouton>
                  {/* La visibilité, à côté du cadenas d'obligation : les deux
                      disent « à quelles conditions ce champ existe ». Le défaut
                      n'est pas neutre — tout est réservé aux correspondants sauf
                      la liste courte patients ; prix et sous-traitance aux
                      laboratoires ; la remarque importante reste interne. */}
                  <select value={niveauAffiche(visibilite[f.key], f.key)}
                    disabled={!estRq} style={{ fontSize: 12.5 }}
                    aria-label={`Visibilité de « ${f.label} »`}
                    title="Qui reçoit ce champ. Appliqué côté serveur : un champ fermé ne sort plus du tout."
                    onChange={(e) => setVisibilite({ ...visibilite, [f.key]: e.currentTarget.value })}>
                    <option value="public">Public (patients)</option>
                    <option value="correspondant">Correspondants connectés</option>
                    <option value="laboratoire">Laboratoires de l’annuaire</option>
                    <option value="interne">Interne</option>
                  </select>
                  <span style={{ display: 'inline-flex', gap: 3 }}>
                    <Bouton taille="sm" title="Monter" onClick={() => bouger(sec, f.key, -1)}>↑</Bouton>
                    <Bouton taille="sm" title="Descendre" onClick={() => bouger(sec, f.key, 1)}>↓</Bouton>
                  </span>
                  {f.custom && estRq && (
                    <Bouton variante="danger" taille="sm" title="Supprimer ce champ ajouté" onClick={() => void retirerChamp(f.key)}>✕</Bouton>
                  )}
                </div>
              );
            })}

            {estRq && (
              <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                <input value={a.libelle} placeholder="Nouveau champ…" style={{ width: 200 }}
                  onChange={(e) => setAjout({ ...ajout, [sec]: { ...a, libelle: e.currentTarget.value } })} />
                <select value={a.type} style={{ fontSize: 12.5 }}
                  onChange={(e) => setAjout({ ...ajout, [sec]: { ...a, type: e.currentTarget.value } })}>
                  {CONFIGURABLE_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <Bouton taille="sm" disabled={!a.libelle.trim()} onClick={() => ajouterChamp(sec)}>Ajouter le champ</Bouton>
              </div>
            )}
          </div>
        );
      })}

      {estRq && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
          <input value={nouvelleSection} placeholder="Nouvelle section…" style={{ width: 200 }}
            onChange={(e) => setNouvelleSection(e.currentTarget.value)} />
          <Bouton taille="sm" disabled={!nouvelleSection.trim() || sections.includes(nouvelleSection.trim())} onClick={() => { setSectionsPersos([...sectionsPersos, nouvelleSection.trim()]); setNouvelleSection(''); }}>
            Ajouter la section
          </Bouton>
        </div>
      )}

      {estRq && <BarreEnregistrer envoi={envoi} onClick={enregistrer} quoi="les champs" />}
    </div>
  );
}

/* ═════════ 2. AFFICHAGES — quels champs pour quel public ═════════════════ */

type Affichage = {
  name: string; roles: string[]; removable: boolean;
  summary: Record<string, boolean>; right: Record<string, boolean>; fiche: Record<string, boolean>;
  masquerIncomplets?: boolean;
};

export function OngletAffichages({ p, estRq, envoi, ecrire }: Props) {
  const { demander, demanderTexte, confirmation } = useConfirmation();
  const [affichages, setAffichages] = useState<Record<string, Affichage>>(
    JSON.parse(JSON.stringify(p.displays || {})),
  );
  const [ordreAff, setOrdreAff] = useState<string[]>(Array.isArray(p.displayOrder) ? [...p.displayOrder] : []);
  const [courant, setCourant] = useState<string>((p.displayOrder || [])[0] || 'interne');

  const persos: FieldDef[] = Array.isArray(p.customFields) ? p.customFields : [];
  const catalogue = [...FIELD_CATALOG, ...persos];
  const actifs = catalogue.filter((f) => (f.core ? true : (p.activeFields || {})[f.key] !== false));
  const profils: string[] = Array.isArray(p.profileOrder) ? p.profileOrder : [];
  const nomProfil = (r: string) => p.profileMeta?.[r]?.label || r;

  const cur = affichages[courant];
  const poserZone = (zone: 'summary' | 'right' | 'fiche', key: string, on: boolean) => {
    if (!cur) return;
    setAffichages({ ...affichages, [courant]: { ...cur, [zone]: { ...cur[zone], [key]: on } } });
  };
  /** Lecture de l'état d'une case. Passée à `Zone`, qui vit maintenant hors du
   *  rendu et n'a donc plus accès à `cur` par fermeture. */
  const coche = (zone: 'summary' | 'right' | 'fiche', key: string) => !!cur?.[zone]?.[key];
  /* Un profil ne vit que dans UN affichage : l'assigner ici le retire de celui
     qui le portait — sinon deux affichages se disputeraient le même lecteur et
     le résultat dépendrait de l'ordre de parcours. */
  const basculerProfil = (r: string) => {
    if (!cur) return;
    const suiv: Record<string, Affichage> = {};
    for (const [id, d] of Object.entries(affichages)) {
      suiv[id] = { ...d, roles: (d.roles || []).filter((x) => x !== r) };
    }
    if (!(cur.roles || []).includes(r)) suiv[courant].roles = [...suiv[courant].roles, r];
    setAffichages(suiv);
  };
  const ajouterAffichage = async () => {
    const nom = await demanderTexte('Nom du nouvel affichage :', { titre: 'Nouvel affichage', confirmer: 'Créer', multiligne: false }); if (!nom?.trim()) return;
    const id = cleDepuisLibelle(nom, new Set(Object.keys(affichages)));
    setAffichages({ ...affichages, [id]: { name: nom.trim(), roles: [], removable: true, summary: {}, right: {}, fiche: {} } });
    setOrdreAff([...ordreAff, id]); setCourant(id);
  };
  const retirerAffichage = async () => {
    if (!cur?.removable) return;
    /* `useConfirmation` et non `window.confirm` (24/08/2026). */
    if (!(await demander(`Supprimer l'affichage « ${cur.name} » ?\nLes profils qui le portaient retomberont sur l'affichage interne.`,
      { confirmer: 'Supprimer' }))) return;
    const suiv = { ...affichages }; delete suiv[courant];
    setAffichages(suiv); setOrdreAff(ordreAff.filter((x) => x !== courant));
    setCourant(ordreAff.find((x) => x !== courant) || 'interne');
  };
  const enregistrer = () => ecrire({ displays: affichages, displayOrder: ordreAff }, 'Affichages enregistrés');

  if (!ordreAff.length) return <EmptyState titre="Aucun affichage" indice="Le seed du paramétrage ne s’est jamais joué." />;
  return (
    <div style={{ ...card, padding: 12 }}>
      {confirmation}
      <div style={etiquette}>Affichages</div>
      <div style={{ ...indice, marginBottom: 10 }}>
        Un affichage dit ce que chaque public voit d’une fiche : le <strong>résumé</strong> (la ligne du
        catalogue), ce qui paraît <strong>à droite</strong> du résumé, et les champs <strong>dans la fiche</strong> ouverte.
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        <Segment ariaLabel="Affichage" valeur={courant} onChange={setCourant} options={ordreAff.map((id) => ({ valeur: id, libelle: affichages[id]?.name || id }))} />
        {estRq && <Bouton taille="sm" onClick={ajouterAffichage}>+ Affichage</Bouton>}
      </div>
      {cur && (
        <>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
            <input value={cur.name} disabled={!estRq} style={{ width: 220 }}
              onChange={(e) => setAffichages({ ...affichages, [courant]: { ...cur, name: e.currentTarget.value } })} />
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5 }}>
              <input type="checkbox" disabled={!estRq} checked={!!cur.masquerIncomplets}
                onChange={(e) => setAffichages({ ...affichages, [courant]: { ...cur, masquerIncomplets: e.currentTarget.checked } })} />
              Masquer les champs non renseignés
            </label>
            {cur.removable && estRq && (
              <Bouton variante="danger" taille="sm" onClick={() => void retirerAffichage()}>Supprimer</Bouton>
            )}
          </div>
          <div style={{ marginBottom: 10 }}>
            <div style={etiquette}>Profils servis par cet affichage</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {profils.map((r) => (
                <Bouton variante={(cur.roles || []).includes(r) ? 'primaire' : 'contour'} taille="sm" key={r} disabled={!estRq} title="Un profil ne vit que dans un affichage : l'assigner ici le retire de l'autre." onClick={() => basculerProfil(r)}>{nomProfil(r)}</Bouton>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
            <Zone zone="summary" titre="Dans le résumé" estRq={estRq} coche={coche} onCocher={poserZone}
              items={actifs.filter((f) => f.key !== 'libelle').map((f) => ({ key: f.key, label: f.label }))} />
            <Zone zone="right" titre="À droite du résumé" estRq={estRq} coche={coche} onCocher={poserZone} items={[
              { key: 'statut', label: 'Statut de la fiche' }, { key: 'completude', label: 'Complétude' },
              ...actifs.filter((f) => f.key !== 'libelle').map((f) => ({ key: f.key, label: f.label })),
            ]} />
            <Zone zone="fiche" titre="Dans la fiche ouverte" estRq={estRq} coche={coche} onCocher={poserZone}
              items={actifs.map((f) => ({ key: f.key, label: f.label }))} />
          </div>
        </>
      )}
      {estRq && <BarreEnregistrer envoi={envoi} onClick={enregistrer} quoi="les affichages" />}
    </div>
  );
}

/* ═════════ 3. STATUTS — les libellés du cycle de vie ═════════════════════ */

const STATUTS: { key: string; defaut: string; bg: string; fg: string }[] = [
  { key: 'brouillon', defaut: 'Brouillon', bg: 'var(--gray-100)', fg: 'var(--gray-700)' },
  { key: 'propose', defaut: 'Proposé', bg: 'var(--amber-50)', fg: 'var(--amber-600)' },
  { key: 'valide', defaut: 'Validé', bg: 'var(--cyan-50)', fg: 'var(--cyan-700)' },
  { key: 'publie', defaut: 'Publiée', bg: 'var(--green-50)', fg: 'var(--green-600)' },
];

export function OngletStatuts({ p, estRq, envoi, ecrire }: Props) {
  const [libelles, setLibelles] = useState<Record<string, string>>({ ...(p.statutLabels || {}) });
  return (
    <div style={{ ...card, padding: 12 }}>
      <div style={etiquette}>Libellés des statuts</div>
      <div style={{ ...indice, marginBottom: 10 }}>
        Ils s’affichent partout — catalogue, circuit, versions. La couleur de chaque statut reste fixe :
        elle porte le sens (gris = en cours, vert = opposable), et la changer ferait mentir les écrans déjà appris.
      </div>
      {STATUTS.map((s) => (
        <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '3px 0' }}>
          <span style={{ width: 100, textAlign: 'center', padding: '4px 8px', borderRadius: 'var(--radius-sm)', fontSize: 12.5, fontWeight: 700, background: s.bg, color: s.fg }}>
            {libelles[s.key] || s.defaut}
          </span>
          <input value={libelles[s.key] ?? s.defaut} disabled={!estRq} placeholder={s.defaut} style={{ width: 240 }}
            onChange={(e) => setLibelles({ ...libelles, [s.key]: e.currentTarget.value })} />
        </div>
      ))}
      {estRq && <BarreEnregistrer envoi={envoi} onClick={() => ecrire({ statutLabels: libelles }, 'Statuts enregistrés')} quoi="les statuts" />}
    </div>
  );
}

/* ═════════ 4. LIVRET — le PDF imprimé ════════════════════════════════════ */

const LIVRET_TEMPLATES = [
  { key: 'institutionnel', nom: 'Institutionnel', desc: 'En-têtes colorés, bandes de famille, page de garde affirmée.' },
  { key: 'sobre', nom: 'Sobre', desc: 'Filets fins, petites capitales — pensé pour l’impression N&B.' },
  { key: 'compact', nom: 'Compact', desc: 'Corps réduit, interlignes serrés — minimise le nombre de pages.' },
];
const LIVRET_COVERS = [
  { key: 'centre', nom: 'Centrée' }, { key: 'bandeau', nom: 'Bandeau' }, { key: 'minimal', nom: 'Minimale' },
];

export function OngletLivret({ p, estRq, envoi, ecrire }: Props) {
  const [livret, setLivret] = useState<Cfg>({
    template: 'institutionnel', cover: 'centre', coverText: '', coverText2: '',
    columns: 2, groupByFamily: true, glossary: true, toc: true, fields: {},
    ...(p.livret || {}),
  });
  const persos: FieldDef[] = Array.isArray(p.customFields) ? p.customFields : [];
  const actifs = [...FIELD_CATALOG, ...persos]
    .filter((f) => (f.core ? true : (p.activeFields || {})[f.key] !== false))
    .filter((f) => f.key !== 'libelle');
  const poser = (patch: Cfg) => setLivret({ ...livret, ...patch });

  return (
    <div style={{ ...card, padding: 12 }}>
      <div style={etiquette}>Livret imprimé</div>
      <div style={{ ...indice, marginBottom: 10 }}>
        Le PDF est composé par la vitrine et <strong>figé à chaque diffusion</strong> de version — l’artefact
        archivé fait foi, pas la page à l’écran. Ces réglages s’appliquent à la PROCHAINE diffusion.
      </div>

      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 240px' }}>
          <div style={etiquette}>Modèle</div>
          {LIVRET_TEMPLATES.map((t) => (
            <label key={t.key} style={{ display: 'flex', gap: 7, alignItems: 'baseline', padding: '3px 0', fontSize: 12.5, cursor: 'pointer' }}>
              <input type="radio" name="livret-template" disabled={!estRq} checked={(livret.template || 'institutionnel') === t.key}
                onChange={() => poser({ template: t.key })} />
              <span><strong>{t.nom}</strong> <span style={indice}>{t.desc}</span></span>
            </label>
          ))}
          <div style={{ ...etiquette, marginTop: 10 }}>Page de garde</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <Segment ariaLabel="Page de couverture" valeur={(livret.cover || 'centre')} onChange={(valeur) => poser({ cover: valeur })} options={LIVRET_COVERS.map((c) => ({ valeur: c.key, libelle: c.nom, disabled: !estRq }))} />
          </div>
          <div style={{ ...etiquette, marginTop: 10 }}>Colonnes</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <Segment ariaLabel="Colonnes du livret" valeur={(livret.columns || 2)} onChange={(valeur) => poser({ columvaleurs: valeur })} options={[1, 2].map((n) => ({ valeur: n, libelle: <>{n}colonne{n > 1 ? 's' : ''}</>, disabled: !estRq }))} />
          </div>
          <div style={{ marginTop: 10 }}>
            <Bascule cle="groupByFamily" label="Cahier à intercalaires (une famille par page)" estRq={estRq} actif={livret["groupByFamily"] !== false} onBasculer={(c, v) => poser({ [c]: v })} />
            <Bascule cle="glossary" label="Glossaire (noms + synonymes) en fin de manuel" estRq={estRq} actif={livret["glossary"] !== false} onBasculer={(c, v) => poser({ [c]: v })} />
            <Bascule cle="toc" label="Sommaire en début de manuel" estRq={estRq} actif={livret["toc"] !== false} onBasculer={(c, v) => poser({ [c]: v })} />
            <Bascule cle="index" label="Index alphabétique en fin de manuel" estRq={estRq} actif={livret["index"] !== false} onBasculer={(c, v) => poser({ [c]: v })} />
          </div>
        </div>

        <div style={{ flex: '1 1 260px' }}>
          <div style={etiquette}>Texte de la page de garde</div>
          <textarea value={livret.coverText || ''} disabled={!estRq} style={{ width: '100%', minHeight: 76, fontSize: 12.5 }}
            onChange={(e) => poser({ coverText: e.currentTarget.value })} />
          <div style={{ ...etiquette, marginTop: 8 }}>Second paragraphe</div>
          <textarea value={livret.coverText2 || ''} disabled={!estRq} style={{ width: '100%', minHeight: 60, fontSize: 12.5 }}
            onChange={(e) => poser({ coverText2: e.currentTarget.value })} />
          <div style={{ ...etiquette, marginTop: 8 }}>Champs imprimés ({actifs.filter((f) => livret.fields?.[f.key] !== false).length}/{actifs.length})</div>
          <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', padding: '4px 8px' }}>
            {actifs.map((f) => (
              <label key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '2px 0', fontSize: 12.5, cursor: 'pointer' }}>
                <input type="checkbox" disabled={!estRq} checked={livret.fields?.[f.key] !== false}
                  onChange={(e) => poser({ fields: { ...(livret.fields || {}), [f.key]: e.currentTarget.checked } })} />
                {f.label}
              </label>
            ))}
          </div>
        </div>
      </div>
      {estRq && <BarreEnregistrer envoi={envoi} onClick={() => ecrire({ livret }, 'Livret enregistré')} quoi="le livret" />}
    </div>
  );
}

/* ═════════ 5. FORFAIT — modules actifs et double authentification ════════ */

const MODULES: { key: string; label: string; desc: string; socle?: string }[] = [
  { key: 'site', label: 'Site public', desc: 'Vitrine, rendez-vous, contact, inscriptions.', socle: 'Le site porte la connexion de la suite.' },
  { key: 'manuel', label: 'Catalogue des analyses', desc: 'Catalogue, versions opposables, livret PDF.', socle: 'Le manuel porte le catalogue de la suite.' },
  { key: 'ged', label: 'Qualité (GED)', desc: 'Circuit documentaire, NC, CAPA, audits, équipements, CQI/EEQ.' },
];
const MODES_2FA: { key: string; label: string }[] = [
  { key: 'google', label: 'Application (TOTP)' }, { key: 'email', label: 'Courriel' },
  { key: 'sms', label: 'SMS' }, { key: 'whatsapp', label: 'WhatsApp' },
];

export function OngletForfait({ p, estRq, envoi, ecrire }: Props) {
  const org = p.org || {};
  const [modules, setModules] = useState<Record<string, boolean>>({ ...(org.modules || {}) });
  const [tfa, setTfa] = useState<Cfg>({ ...(org.twoFactor || {}) });

  return (
    <div style={{ ...card, padding: 12 }}>
      <div style={etiquette}>Forfait du laboratoire</div>
      <div style={{ ...indice, marginBottom: 10 }}>
        Seuls les modules et la double authentification s’écrivent ici. La dénomination, le logo, les
        couleurs et les comptes se règlent dans le module <strong>Site</strong> — commun à toute la suite.
      </div>
      {MODULES.map((m) => {
        const on = m.socle ? true : modules[m.key] !== false;
        return (
          <label key={m.key} title={m.socle || ''}
            style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '4px 0', fontSize: 13, cursor: m.socle ? 'default' : 'pointer', opacity: on ? 1 : 0.55 }}>
            <input type="checkbox" checked={on} disabled={!estRq || !!m.socle}
              onChange={(e) => setModules({ ...modules, [m.key]: e.currentTarget.checked })} />
            <span><strong>{m.label}</strong>{m.socle && <span style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-faint)', marginLeft: 6 }}>Socle</span>}
              <br /><span style={indice}>{m.desc}</span></span>
          </label>
        );
      })}
      <div style={{ ...etiquette, marginTop: 12 }}>Double authentification</div>
      <div style={{ ...indice, marginBottom: 6 }}>
        Désactivée par défaut — c’est l’état voulu tant que le laboratoire n’a pas déployé les téléphones.
        Le code est demandé APRÈS le mot de passe, à chaque connexion.
      </div>
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13, cursor: 'pointer' }}>
        <input type="checkbox" disabled={!estRq} checked={!!tfa.enabled}
          onChange={(e) => setTfa({ ...tfa, enabled: e.currentTarget.checked })} />
        Exiger un second facteur à la connexion
      </label>
      {tfa.enabled && (
        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
          <Segment ariaLabel="Mode de double authentification" valeur={(tfa.mode || 'google')} onChange={(valeur) => setTfa({ ...tfa, mode: valeur })} options={MODES_2FA.map((m) => ({ valeur: m.key, libelle: m.label, disabled: !estRq }))} />
        </div>
      )}
      {estRq && (
        <BarreEnregistrer envoi={envoi} quoi="le forfait"
          onClick={() => ecrire({ forfait: { modules, twoFactor: tfa } }, 'Forfait enregistré')} />
      )}
    </div>
  );
}
